import { type Chart, type ChartArea, type Scale } from "chart.js";

import {
  type generateChartDataset,
  getThemeColors,
  groupDatasets,
} from "../chart-utils";

export const TitlesPlugin = {
  id: "TitlesPlugin",

  afterDraw(chart: Chart, _args: any) {
    const { ctx, chartArea, config, scales } = chart;

    const chartDatasets = config.data.datasets as unknown as ReturnType<
      typeof generateChartDataset
    >["datasets"];

    const grouppedDatasets = groupDatasets(chartDatasets);
    let visibleAxesCount = 0;

    for (const { datasets } of Object.values(grouppedDatasets)) {
      const isVisible = datasets.some((e) => !e.hidden);

      if (isVisible) {
        visibleAxesCount += 1;
      }
    }

    let index = -1;

    for (const [groupId, { title: datasetTitle, datasets }] of Object.entries(
      grouppedDatasets,
    )) {
      const isScaleVisible = datasets.some(
        (e) => e.datasetType === "metric" && !e.hidden,
      );

      if (!isScaleVisible) {
        continue;
      }

      index += 1;

      if (index > 2) {
        continue;
      }

      const maxLabelLength = 30;
      const ellipsis = "...";
      const title =
        datasetTitle.length > maxLabelLength
          ? datasetTitle.slice(0, maxLabelLength) + ellipsis
          : datasetTitle;
      const scale = scales[`y:${groupId}`];

      drawTitle({
        ctx,
        chartArea,
        scale,
        title,
        drawBrackets: scale.position === "right" && visibleAxesCount >= 3,
      });
    }
  },
};

const drawTitle = ({
  ctx,
  chartArea,
  scale,
  title,
  drawBrackets,
}: {
  ctx: CanvasRenderingContext2D;
  chartArea: ChartArea;
  scale: Scale;
  title: string;
  drawBrackets?: boolean;
}) => {
  const left = scale.position === "left" ? scale.left + 10 : scale.right - 10;
  const tickEnd = scale.position === "left" ? left + 8 : left - 8;

  ctx.save();

  const themeColors = getThemeColors();
  if (drawBrackets) {
    ctx.strokeStyle = themeColors.borderTertiary;
    ctx.lineWidth = 1;

    drawLine(ctx, left, chartArea.top, tickEnd, chartArea.top);
    drawLine(ctx, left, chartArea.top, left, chartArea.bottom);
    drawLine(ctx, left, chartArea.bottom, tickEnd, chartArea.bottom);
  }

  ctx.font = "600 12px Inter";

  const measuredText = ctx.measureText(title);
  const textWidth = measuredText.width;
  const textHeight =
    measuredText.actualBoundingBoxAscent +
    measuredText.actualBoundingBoxDescent;

  ctx.fillStyle = themeColors.backgroundPrimary;

  ctx.fillRect(
    left - textHeight / 2,
    (chartArea.top + chartArea.bottom) / 2 - textWidth / 2 - 10,
    textHeight,
    textWidth + 20,
  );

  ctx.fillStyle = themeColors.textPrimary;
  ctx.translate(
    left + textHeight / 2,
    (chartArea.top + chartArea.bottom) / 2 + textWidth / 2,
  );
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(title, 0, 0);
  ctx.restore();
};

const drawLine = (
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
) => {
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();
};
